import React, {useState, useEffect} from "react";
import {useAuth} from "../contexts/AuthContext";
import {
  getExportJobStatus,
  downloadExportData,
  ExportJob,
} from "../services/api";
import Navigation from "./Navigation";
import "../App.css";

const ExportJobs: React.FC = () => {
  const {user} = useAuth();
  const [jobs, setJobs] = useState<ExportJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState<Record<string, boolean>>({});
  const [isIndexCreating, setIsIndexCreating] = useState(false);

  useEffect(() => {
    loadJobs();
    // Poll for updates every 5 seconds
    const interval = setInterval(loadJobs, 5000);
    return () => clearInterval(interval);
  }, [user]);

  const loadJobs = async () => {
    if (!user) return;

    try {
      const response = await getExportJobStatus(undefined, user.uid);
      if (response.jobs) {
        setJobs(response.jobs);
      } else {
        setJobs([]);
      }
      setError("");
    } catch (err: any) {
      const errorMessage = err.message || "Failed to load jobs";
      // Check if it's an index error
      if (errorMessage.includes("index") || errorMessage.includes("FAILED_PRECONDITION")) {
        setIsIndexCreating(true);
        setError("Database index is being created. This usually takes 1-2 minutes. Please wait a moment and refresh.");
      } else {
        setIsIndexCreating(false);
        setError(errorMessage);
      }
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (jobId: string) => {
    try {
      await downloadExportData(jobId);
    } catch (err: any) {
      setError(err.message || "Failed to download export");
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const getStatusBadge = (status: string) => {
    const statusClass = `status-badge status-${status}`;
    return <span className={statusClass}>{status.toUpperCase()}</span>;
  };

  if (loading) {
    return (
      <div>
        <Navigation />
        <div className="container">
          <div className="text-center">
            <div className="spinner"></div>
            <p>Loading jobs...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navigation />
      <div className="container">
        <div className="card">
          <div className="card-header">
            <h1 className="card-title">Export Jobs</h1>
            <button
              className="btn btn-secondary"
              onClick={loadJobs}
              disabled={loading}
            >
              Refresh
            </button>
          </div>

          {error && (
            <div className={`alert ${isIndexCreating ? "alert-info" : "alert-error"}`}>
              {error}
              {isIndexCreating && (
                <div style={{marginTop: "0.75rem", fontSize: "0.875rem"}}>
                  <p>While waiting, you can:</p>
                  <ul style={{marginLeft: "1.5rem", marginTop: "0.5rem"}}>
                    <li>Go to the Dashboard and prepare your export</li>
                    <li>Check back in a few minutes</li>
                  </ul>
                </div>
              )}
            </div>
          )}

          {!error && jobs.length === 0 && !loading ? (
            <div className="empty-state">
              <div className="empty-state-icon">📦</div>
              <p>No export jobs found</p>
              <p style={{fontSize: "0.875rem", marginTop: "0.5rem", color: "#666"}}>
                Create an export from the Dashboard
              </p>
            </div>
          ) : jobs.length > 0 ? (
            <div className="job-list">
              {jobs.map((job) => (
                <div key={job.id} className="job-item">
                  <div className="job-header">
                    <div>
                      <div className="flex gap-2" style={{alignItems: "center"}}>
                        <strong>Job #{job.id?.substring(0, 8)}</strong>
                        {getStatusBadge(job.status)}
                      </div>
                      <div className="job-meta">
                        <span>
                          <strong>Users:</strong> {job.selectedUsers.length}
                        </span>
                        <span>
                          <strong>Created:</strong> {formatDate(job.createdAt)}
                        </span>
                        {job.updatedAt && (
                          <span>
                            <strong>Updated:</strong> {formatDate(job.updatedAt)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {job.status === "completed" && (
                        <>
                          <button
                            className="btn btn-success"
                            onClick={() => handleDownload(job.id!)}
                          >
                            Download ZIP
                          </button>
                          {job.zipPassword && (
                            <button
                              className="btn btn-secondary"
                              onClick={() =>
                                setShowPassword({
                                  ...showPassword,
                                  [job.id!]: !showPassword[job.id!],
                                })
                              }
                            >
                              {showPassword[job.id!] ? "Hide" : "Show"} Password
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {job.status === "in_progress" && (
                    <div className="progress-container">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{width: `${job.progress}%`}}
                        >
                          {job.progress}%
                        </div>
                      </div>
                      <div className="job-meta">
                        <span>
                          Processed: {job.processedItems} / {job.totalItems}
                        </span>
                      </div>
                    </div>
                  )}

                  {job.error && (
                    <div className="alert alert-error mt-2">
                      <strong>Error:</strong> {job.error}
                    </div>
                  )}

                  {job.status === "completed" && job.zipPassword && showPassword[job.id!] && (
                    <div className="alert alert-info mt-2">
                      <strong>ZIP Password:</strong>
                      <div
                        style={{
                          marginTop: "0.5rem",
                          fontFamily: "monospace",
                          fontSize: "1.1rem",
                          fontWeight: "bold",
                          backgroundColor: "#f8f9fa",
                          padding: "0.75rem",
                          borderRadius: "4px",
                          border: "1px solid #dee2e6",
                        }}
                      >
                        {job.zipPassword}
                      </div>
                      <small style={{display: "block", marginTop: "0.5rem", color: "#666"}}>
                        This password is required to extract the ZIP file. Please keep it secure.
                      </small>
                    </div>
                  )}

                  {job.status === "completed" && job.exportedData && (
                    <div className="mt-2" style={{fontSize: "0.875rem", color: "#666"}}>
                      <strong>Exported:</strong>{" "}
                      {job.exportedData.conversations?.length || 0} conversations,{" "}
                      {job.exportedData.files?.length || 0} files,{" "}
                      {job.exportedData.gpts?.length || 0} GPTs,{" "}
                      {job.exportedData.memories?.length || 0} memories
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ExportJobs;

